import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, TrendingUp, ShieldCheck, AlertTriangle, ChevronRight, HelpCircle, ArrowRight } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

const TYPE_LABEL = {
  equity: "Equity",
  debt: "Debt",
  RBF: "Revenue-Based Finance",
  blended_finance: "Blended Finance",
  trade_finance: "Trade Finance",
  asset_finance: "Asset Finance",
  grants: "Grants",
};

const SCORE_COLOR = (s) =>
  s >= 75 ? "text-[#0A8B85]"
  : s >= 55 ? "text-[#0A8B85]"
  : s >= 40 ? "text-amber-600"
  : "text-red-600";

function Score({ label, value, hint, tooltip, liftHref, testid }) {
  return (
    <div data-testid={testid}
         className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex items-center gap-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-700">{label}</div>
        {tooltip && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`What is ${label}?`}
                  data-testid={`${testid}-help`}
                  className="rounded-full p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-[#0A8B85] focus:outline-none focus:ring-2 focus:ring-[#0A8B85]/40"
                >
                  <HelpCircle className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                sideOffset={6}
                className="max-w-xs whitespace-normal bg-neutral-900 text-left text-[12px] leading-snug text-white"
              >
                <div className="space-y-2">
                  <div>{tooltip}</div>
                  {liftHref && (
                    <Link
                      to={liftHref}
                      data-testid={`${testid}-lift-link`}
                      className="inline-flex items-center gap-1 rounded-md bg-[#0A8B85] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#066667]"
                    >
                      How to lift this score
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className={`mt-1 text-3xl font-extrabold ${SCORE_COLOR(value || 0)}`}>
        {value || 0}<span className="text-base text-neutral-400">/100</span>
      </div>
      {hint && <div className="mt-1.5 text-[11px] leading-snug text-neutral-600">{hint}</div>}
    </div>
  );
}

function CategoryRow({ entry, openIdx, setOpenIdx, idx }) {
  const expanded = openIdx === idx;
  const fit = entry.fit_score || 0;
  return (
    <div data-testid={`cap-match-${idx}`} className="rounded-xl border border-neutral-200 bg-white">
      <button type="button" onClick={() => setOpenIdx(expanded ? null : idx)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50">
        <div className="flex items-center gap-3">
          <span className={`flex h-9 w-12 items-center justify-center rounded-md text-sm font-extrabold ${SCORE_COLOR(fit)} bg-neutral-100`}>
            {fit}
          </span>
          <div>
            <div className="text-sm font-semibold text-neutral-900">{entry.funder_category}</div>
            <div className="text-[11px] text-neutral-500">Fit score {fit}/100 — how closely this funder type matches your business today.</div>
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-neutral-400 transition-transform ${expanded ? "rotate-90" : ""}`}/>
      </button>
      {expanded && (
        <div className="space-y-3 border-t border-neutral-200 px-4 py-3">
          {(entry.match_reasons || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#0A8B85]">Why you fit</div>
              <ul className="mt-1 space-y-0.5 text-xs text-neutral-700">
                {entry.match_reasons.map((r) => <li key={r}>• {r}</li>)}
              </ul>
            </div>
          )}
          {(entry.gaps || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Gaps to close</div>
              <ul className="mt-1 space-y-0.5 text-xs text-neutral-700">
                {entry.gaps.map((r) => <li key={r}>• {r}</li>)}
              </ul>
            </div>
          )}
          {(entry.matched_funders || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Real funders in this category</div>
              <div className="mt-1 space-y-1">
                {entry.matched_funders.map((f) => (
                  <div key={f.funder_id} className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-neutral-900">{f.name}</div>
                      <div className="truncate text-[11px] text-neutral-500">
                        {f.type} · {(f.countries || []).slice(0, 3).join(", ") || "global"}
                        {f.ticket_max ? ` · up to $${(f.ticket_max / 1000).toFixed(0)}K` : ""}
                      </div>
                    </div>
                    {f.website && (
                      <a href={f.website} target="_blank" rel="noreferrer"
                         className="shrink-0 text-xs font-semibold text-[#0A8B85] hover:text-[#066667]">↗</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CapitalStructurePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/companies/me/capital-structure");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setData({ error: e?.response?.data?.detail || "Couldn't load" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div data-testid="cap-loading" className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3" aria-busy="true">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-2">
          <div className="h-3 w-32 animate-pulse rounded bg-neutral-100"/>
          <div className="h-5 w-44 animate-pulse rounded bg-neutral-100"/>
          <div className="h-4 w-72 animate-pulse rounded bg-neutral-100"/>
        </div>
        <div className="h-12 w-20 animate-pulse rounded-lg bg-neutral-100"/>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="h-24 animate-pulse rounded-xl bg-neutral-100"/>
        <div className="h-24 animate-pulse rounded-xl bg-neutral-100"/>
        <div className="h-24 animate-pulse rounded-xl bg-neutral-100"/>
      </div>
      <div
        className="mt-4 h-2 w-full overflow-hidden rounded-full bg-neutral-100"
        role="progressbar"
        aria-label="Loading capital structure"
        data-testid="cap-loading-bar"
      >
        <div
          className="h-2 w-1/3 rounded-full bg-gradient-to-r from-neutral-200 via-[#0A8B85]/70 to-neutral-200"
          style={{ animation: "scoreShimmer 1.4s ease-in-out infinite" }}
        />
      </div>
      <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin"/> Loading capital structure…
      </p>
    </div>
  );
  // Hide silently if nothing to show (no v2.1 analysis yet)
  if (!data || data.error || (!data.recommended_structure?.primary_type && !(data.funder_match_list || []).length)) return null;

  const crs = data.readiness_score || 0;
  const drs = data.debt_readiness_score || 0;
  const ers = data.equity_readiness_score || 0;
  const rbfrs = data.rbf_readiness_score || 0;
  const rec = data.recommended_structure || {};
  const flags = data.capital_structure_flags || [];
  const viable = data.capital_types_viable || [];

  return (
    <div data-testid="capital-structure-panel"
         className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#0A8B85]">Capital Structure</p>
          <h2 className="mt-1 text-xl font-bold text-neutral-900">Your funder roadmap</h2>
          <p className="mt-2 text-sm leading-relaxed text-neutral-600">
            Different funders look for different things. Below are three
            <span className="font-semibold text-neutral-800"> readiness sub-scores</span> —
            each one tells you, out of 100, how prepared your business
            currently looks to a specific type of funder. The higher the
            score, the stronger your odds of being funded by that kind of
            investor.
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Overall Readiness</span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="What is Overall Readiness?"
                    data-testid="overall-readiness-help"
                    className="rounded-full p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-[#0A8B85] focus:outline-none focus:ring-2 focus:ring-[#0A8B85]/40"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="end"
                  sideOffset={6}
                  className="max-w-xs whitespace-normal bg-neutral-900 text-left text-[12px] leading-snug text-white"
                >
                  <div className="space-y-2">
                    <div>
                      Your <span className="font-semibold">Overall Readiness</span> blends the three sub-scores below into a single 0–100 number, weighted by which capital types fit your business right now.
                      <br />
                      Lifting any sub-score lifts this number too.
                    </div>
                    <Link
                      to="/founder/readiness-report#score-breakdown"
                      data-testid="overall-readiness-lift-link"
                      className="inline-flex items-center gap-1 rounded-md bg-[#0A8B85] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#066667]"
                    >
                      View full breakdown
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className={`text-3xl font-extrabold ${SCORE_COLOR(crs)}`}>{crs}<span className="text-base text-neutral-400">/100</span></div>
          <div className="text-[10px] text-neutral-500">Your blended score across all funder types.</div>
        </div>
      </div>

      {/* Viable types */}
      {viable.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="cap-viable-types">
          <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mr-1 self-center">
            Viable capital types:
          </span>
          {viable.map((t) => (
            <span key={t} className="rounded-full bg-[#0A8B85]/10 px-2.5 py-1 text-[11px] font-semibold text-[#0A8B85]">
              {TYPE_LABEL[t] || t}
            </span>
          ))}
        </div>
      )}

      {/* Sub-scores */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Score
          label="Debt Readiness"
          value={drs}
          hint="How loan-ready you look to banks and DFIs."
          liftHref="/founder/readiness-report#pillar-row-P1"
          tooltip={
            <>
              Measures how ready your business looks to <span className="font-semibold">banks and development finance institutions (DFIs)</span> offering loans.
              <br />
              Driven by revenue stability, collateral or guarantees, and how clean your financial reporting is.
            </>
          }
          testid="cap-sub-drs"
        />
        <Score
          label="Equity Readiness"
          value={ers}
          hint="How investable you look to angels, VCs, and PE."
          liftHref="/founder/readiness-report#pillar-row-P2"
          tooltip={
            <>
              Measures how ready your business looks to <span className="font-semibold">angels, venture capital, and private-equity</span> investors offering risk capital.
              <br />
              Driven by growth rate, market opportunity, and the strength of your team.
            </>
          }
          testid="cap-sub-ers"
        />
        <Score
          label="Revenue-Based Finance Readiness"
          value={rbfrs}
          hint="How fundable you look to revenue-share investors."
          liftHref="/founder/readiness-report#pillar-row-P1"
          tooltip={
            <>
              Measures how ready you look to <span className="font-semibold">revenue-based finance</span> (RBF) investors — they get repaid as a share of your monthly revenue rather than taking equity.
              <br />
              Typically needs steady monthly revenue above ~$20K and predictable gross margins.
            </>
          }
          testid="cap-sub-rbfrs"
        />
      </div>

      {/* Recommended structure */}
      {rec.primary_type && (
        <div className="rounded-xl border border-[#0A8B85]/30 bg-[#0A8B85]/5 p-4" data-testid="cap-recommended">
          <div className="flex items-center gap-2 text-[#0A8B85]">
            <TrendingUp className="h-4 w-4"/>
            <span className="text-[10px] font-bold uppercase tracking-wider">Recommended structure</span>
          </div>
          <div className="mt-2 text-sm text-neutral-900">
            <span className="font-bold text-[#0A8B85]">{rec.primary_type}</span>
            {rec.secondary_type && <span className="text-neutral-500"> · then {rec.secondary_type}</span>}
          </div>
          {rec.rationale && <p className="mt-2 text-xs leading-5 text-neutral-700">{rec.rationale}</p>}
        </div>
      )}

      {/* Ranked categories */}
      {(data.funder_match_list || []).length > 0 && (
        <div data-testid="cap-match-list">
          <div className="mb-2 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-sky-700"/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700">
              Ranked funder categories ({data.funder_match_list.length})
            </span>
          </div>
          <p className="mb-3 text-xs leading-relaxed text-neutral-600">
            These are the types of funders most likely to back a business like
            yours, ranked by how well you fit them today. Click a row to see
            why you fit, what would strengthen the case, and what to fix next.
          </p>
          <div className="space-y-2">
            {data.funder_match_list.map((e, i) => (
              <CategoryRow key={e.funder_category || `cat-${i}`} entry={e} idx={i} openIdx={openIdx} setOpenIdx={setOpenIdx}/>
            ))}
          </div>
        </div>
      )}

      {/* Flags */}
      {flags.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="cap-flags">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-4 w-4"/>
            <span className="text-[10px] font-bold uppercase tracking-wider">Capital structure flags ({flags.length})</span>
          </div>
          <ul className="mt-2 space-y-1 text-xs leading-5 text-neutral-700">
            {flags.slice(0, 8).map((f) => <li key={f}>• {f}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
